import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { getProductionOrders, updateProduction } from "services/production";
import { productionStages, stageIndex } from "shared/productionStages";
import store from "data/trayStore.json";
import Icon from "./ProductionIcon";

const defaults = { stage: "all", payment: "all", status: "active", from: "", to: "" };
const cancelled = (order) => /cancel/i.test(order.status || "");
const paid = (order) =>
  /^(pago|paid|aprovado|approved|pagamento aprovado|pagamento confirmado)$/i.test(
    (order.paymentStatus || "").trim()
  );
const money = (value) =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const date = (value) =>
  value
    ? new Date(String(value).slice(0, 10) + "T12:00:00").toLocaleDateString("pt-BR")
    : "Não informada";
const stageLabel = (key) => productionStages.find((stage) => stage.key === key)?.label || key;
const stageColor = (key) =>
  ({
    received: "default",
    printing: "info",
    finishing: "warning",
    shipped: "primary",
    delivered: "success",
  }[key] || "default");
function exportPage(orders) {
  const escape = (value) =>
    `"${String(value ?? "")
      .replace(/^(?=\s*[=+@-]|[\t\r\n])/, "'")
      .replace(/"/g, '""')}"`;
  const rows = [
    [
      "Pedido",
      "Data",
      "Situação",
      "Pagamento",
      "Total",
      "Obra",
      "Referência",
      "Etapa",
      "Rastreamento",
    ],
  ];
  orders.forEach((order) =>
    (order.production?.length
      ? order.production
      : [
          {
            title: order.books?.map((book) => book.title).join(" / "),
            code: order.books?.map((book) => book.reference).join(" / "),
            stage: "Acompanhar na Tray",
          },
        ]
    ).forEach((item) =>
      rows.push([
        order.id,
        date(order.date),
        order.status,
        order.paymentStatus,
        order.total,
        item.title,
        item.code,
        stageLabel(item.stage),
        order.trackingNumber,
      ])
    )
  );
  const url = URL.createObjectURL(
    new Blob(["\uFEFF" + rows.map((row) => row.map(escape).join(";")).join("\r\n")], {
      type: "text/csv;charset=utf-8",
    })
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = "producao-pagina.csv";
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export default function ProductionAdmin() {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState(defaults);
  const [result, setResult] = useState({ orders: [], total: 0 });
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [review, setReview] = useState(null);
  const [detailKey, setDetailKey] = useState(null);
  const [note, setNote] = useState("");
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getProductionOrders(page, query, filters)
      .then((body) => {
        if (active) setResult(body);
      })
      .catch((failure) => {
        if (active) {
          setError(failure.message);
          setResult({ orders: [], total: 0 });
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [page, query, filters, revision]);
  const change = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };
  const reload = () => {
    setQuery(search.trim());
    setPage(1);
    setRevision((value) => value + 1);
  };
  const begin = (order, item) => {
    setReview({ orderId: order.id, item, next: productionStages[stageIndex(item.stage) + 1] });
    setNote("");
    setError("");
  };
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await updateProduction(review.orderId, review.item.code, {
        stage: review.next.key,
        version: review.item.version,
        note,
      });
      setSuccess(`Pedido #${review.orderId}: ${review.next.label.toLowerCase()} registrado.`);
      setReview(null);
      setRevision((value) => value + 1);
    } catch (failure) {
      setError(failure.message);
    } finally {
      setBusy(false);
    }
  };
  const detail = result.orders.find((order) => `${order.id}-${order.ownerUid}` === detailKey);
  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      setSuccess("Copiado para a área de transferência.");
    } catch (_) {
      setError("Não foi possível copiar. Selecione e copie o número exibido.");
    }
  };
  return (
    <Box className="production-workspace">
      <Stack
        className="production-heading"
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography className="workspace-eyebrow">OPERAÇÃO · PEDIDOS</Typography>
          <Typography variant="h5">Produção e entrega</Typography>
          <Typography variant="body2" color="text.secondary">
            Da impressão à entrega, acompanhe cada obra e registre o andamento.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<Icon>download</Icon>}
            disabled={loading || !result.orders.length}
            onClick={() => exportPage(result.orders)}
          >
            Exportar página
          </Button>
          <Button
            variant="contained"
            color="info"
            startIcon={<Icon>refresh</Icon>}
            disabled={loading}
            onClick={reload}
          >
            Atualizar
          </Button>
        </Stack>
      </Stack>
      <Box
        className="production-metrics"
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        {[
          ["Pedidos encontrados", result.total || 0, "receipt_long"],
          [
            "Em produção · nesta página",
            result.orders
              .flatMap((order) => order.production || [])
              .filter((item) => ["printing", "finishing"].includes(item.stage)).length,
            "print",
          ],
          ["Pagos · nesta página", result.orders.filter(paid).length, "payments"],
          [
            "Na Tray · nesta página",
            result.orders.filter((order) => !order.production?.length).length,
            "inventory_2",
          ],
        ].map(([label, count, icon]) => (
          <Card className="production-metric" key={label} sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h4">{loading ? "—" : count}</Typography>
              <Icon sx={{ color: "#138735" }}>{icon}</Icon>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          </Card>
        ))}
      </Box>
      <Card className="production-filters" sx={{ p: 2.5, mb: 3 }}>
        <Box
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            reload();
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2}>
            <TextField
              fullWidth
              placeholder="Buscar pedido, título, autor ou código…"
              inputProps={{ "aria-label": "Pedido, título, autor ou código da obra" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon>search</Icon>
                  </InputAdornment>
                ),
              }}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Button
              type="submit"
              variant="contained"
              color="info"
              startIcon={<Icon>search</Icon>}
              disabled={loading}
              sx={{ minWidth: 120 }}
            >
              Buscar
            </Button>
            <Button
              className="filter-toggle"
              variant="outlined"
              onClick={() => setFiltersOpen((value) => !value)}
              startIcon={<Icon>tune</Icon>}
              aria-expanded={filtersOpen}
            >
              Filtros<Icon sx={{ ml: 1 }}>{filtersOpen ? "expand_less" : "expand_more"}</Icon>
            </Button>
          </Stack>
          <Collapse in={filtersOpen}>
            <Box
              className="production-advanced-filters"
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
                gap: 2,
              }}
            >
              <TextField
                select
                label="Pagamento"
                value={filters.payment}
                onChange={(event) => change("payment", event.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="paid">Pago</MenuItem>
                <MenuItem value="pending">Sem pagamento confirmado</MenuItem>
              </TextField>
              <TextField
                select
                label="Situação do pedido"
                value={filters.status}
                onChange={(event) => change("status", event.target.value)}
              >
                <MenuItem value="active">Não cancelados</MenuItem>
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="cancelled">Cancelados</MenuItem>
              </TextField>
              <TextField
                type="date"
                label="Data inicial"
                InputLabelProps={{ shrink: true }}
                value={filters.from}
                onChange={(event) => change("from", event.target.value)}
              />
              <TextField
                type="date"
                label="Data final"
                InputLabelProps={{ shrink: true }}
                value={filters.to}
                onChange={(event) => change("to", event.target.value)}
              />
            </Box>
          </Collapse>
          <Stack
            className="production-stages"
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            mt={2}
          >
            {[
              { key: "all", label: "Todas as etapas" },
              ...productionStages,
              { key: "untracked", label: "Sem acompanhamento PSO" },
            ].map((stage) => (
              <Chip
                key={stage.key}
                className={filters.stage === stage.key ? "stage-pill is-selected" : "stage-pill"}
                label={stage.label}
                color={filters.stage === stage.key ? "info" : "default"}
                variant={filters.stage === stage.key ? "filled" : "outlined"}
                onClick={() => change("stage", stage.key)}
              />
            ))}
            <Button
              size="small"
              onClick={() => {
                setSearch("");
                setQuery("");
                setFilters({ ...defaults, status: "all" });
                setPage(1);
              }}
            >
              Limpar filtros
            </Button>
          </Stack>
        </Box>
      </Card>
      {error && !review && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}
      <Card className="production-orders" sx={{ overflow: "hidden" }}>
        <Stack
          className="orders-toolbar"
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography variant="h6">Fila de pedidos</Typography>
            <Typography variant="body2">
              Consulte os detalhes ou avance uma obra para a próxima etapa.
            </Typography>
          </Box>
          <Chip label={`${result.total || 0} registros`} size="small" />
        </Stack>
        {loading ? (
          <Stack alignItems="center" p={5}>
            <CircularProgress aria-label="Carregando pedidos" />
          </Stack>
        ) : (
          <>
            <TableContainer>
              <Table size="small" sx={{ minWidth: 850 }}>
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    {["Pedido", "Obras e andamento", "Pagamento / total", "Ações"].map((label) => (
                      <TableCell key={label}>{label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.orders.map((order) => (
                    <TableRow
                      hover
                      key={`${order.id}-${order.ownerUid}`}
                      sx={{ "& td": { py: 2, verticalAlign: "top" } }}
                    >
                      <TableCell>
                        <Typography variant="subtitle2">#{order.id}</Typography>
                        <Typography variant="caption" display="block">
                          {date(order.date)}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={cancelled(order) ? "error" : "default"}
                          label={order.status || "Sem situação"}
                          sx={{ mt: 1, maxWidth: 210 }}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 290 }}>
                        {order.production?.length ? (
                          <Stack spacing={2}>
                            {order.production.map((item) => (
                              <Box key={item.code}>
                                <Typography variant="subtitle2">{item.title || "Obra"}</Typography>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="text.secondary"
                                >
                                  {item.code}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                                  <Chip
                                    size="small"
                                    color={stageColor(item.stage)}
                                    label={stageLabel(item.stage)}
                                  />
                                  {productionStages[stageIndex(item.stage) + 1] && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={cancelled(order)}
                                      onClick={() => begin(order, item)}
                                    >
                                      {productionStages[stageIndex(item.stage) + 1].label}
                                      <Icon sx={{ ml: 0.5 }}>arrow_forward</Icon>
                                    </Button>
                                  )}
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        ) : (
                          <Box>
                            <Typography variant="subtitle2">
                              {order.books?.map((book) => book.title).join(" · ") ||
                                "Publicação anterior"}
                            </Typography>
                            <Chip size="small" label="Gerenciado na Tray" sx={{ mt: 1 }} />
                            <Typography variant="caption" display="block" mt={1}>
                              Acompanhamento disponível no painel da loja.
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={paid(order) ? "success" : "warning"}
                          label={order.paymentStatus || "Não informado"}
                        />
                        <Typography variant="subtitle2" mt={1}>
                          {money(order.total)}
                        </Typography>
                        <Typography variant="caption">Total do pedido na Tray</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack
                          className="order-actions"
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Icon>visibility</Icon>}
                            onClick={() => setDetailKey(`${order.id}-${order.ownerUid}`)}
                          >
                            Detalhes
                          </Button>
                          <Tooltip title="Copiar número do pedido">
                            <IconButton
                              aria-label={`Copiar número do pedido ${order.id}`}
                              onClick={() => copy(order.id)}
                            >
                              <Icon>content_copy</Icon>
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Abrir painel Tray">
                            <IconButton
                              component="a"
                              aria-label={`Abrir painel Tray para consultar pedido ${order.id}`}
                              href={`${store.storeHost}/admin/`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Icon>open_in_new</Icon>
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {!error && !result.orders.length && (
              <Stack alignItems="center" spacing={1} p={5}>
                <Icon sx={{ fontSize: 42, color: "#8ba393" }}>search_off</Icon>
                <Typography variant="h6">Nenhum pedido encontrado</Typography>
                <Typography variant="body2">
                  Ajuste a busca, o período ou as etapas selecionadas.
                </Typography>
              </Stack>
            )}
          </>
        )}
        <Stack direction="row" justifyContent="space-between" alignItems="center" p={2} spacing={1}>
          <Typography variant="caption">
            Página {page} · {result.total || 0} registros encontrados
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button disabled={page === 1 || loading} onClick={() => setPage((value) => value - 1)}>
              Anterior
            </Button>
            <Button
              disabled={!result.hasMore || loading}
              onClick={() => setPage((value) => value + 1)}
            >
              Próxima
            </Button>
          </Stack>
        </Stack>
      </Card>
      <Dialog open={Boolean(detail)} onClose={() => setDetailKey(null)} fullWidth maxWidth="md">
        <DialogTitle>Detalhes do pedido #{detail?.id}</DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Stack spacing={3}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                <Box>
                  <Typography variant="overline">Pagamento</Typography>
                  <Typography variant="body2">
                    {detail.paymentStatus || "Não informado"} ·{" "}
                    {detail.paymentMethod || "Meio não informado"}
                  </Typography>
                  <Typography variant="h6">{money(detail.total)}</Typography>
                </Box>
                <Box>
                  <Typography variant="overline">Entrega</Typography>
                  <Typography variant="body2">
                    {detail.shippingMethod || "Forma de envio não informada"}
                  </Typography>
                  <Typography variant="body2">
                    Rastreamento: {detail.trackingNumber || "Ainda não informado"}
                  </Typography>
                  {detail.trackingNumber && (
                    <Button size="small" onClick={() => copy(detail.trackingNumber)}>
                      Copiar rastreamento
                    </Button>
                  )}
                </Box>
              </Stack>
              {cancelled(detail) && (
                <Alert severity="warning">
                  Este pedido está cancelado. O avanço da produção está bloqueado.
                </Alert>
              )}
              {!detail.production?.length && (
                <Alert severity="info">
                  Este pedido contém publicações anteriores ao código PSO. Consulte e gerencie seu
                  andamento no painel Tray usando o número #{detail.id}.
                </Alert>
              )}
              {detail.production?.map((item) => (
                <Box key={item.code} sx={{ border: "1px solid #dce5df", p: 2, borderRadius: 2 }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Box>
                      <Typography variant="h6">{item.title}</Typography>
                      <Typography variant="caption">{item.code}</Typography>
                    </Box>
                    <Chip color={stageColor(item.stage)} label={stageLabel(item.stage)} />
                  </Stack>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", my: 2 }}>
                    {productionStages.map((stage, index) => (
                      <Chip
                        key={stage.key}
                        size="small"
                        variant={index <= stageIndex(item.stage) ? "filled" : "outlined"}
                        color={index <= stageIndex(item.stage) ? "success" : "default"}
                        icon={
                          <Icon>
                            {index < stageIndex(item.stage) ? "check" : "radio_button_unchecked"}
                          </Icon>
                        }
                        label={stage.label}
                      />
                    ))}
                  </Box>
                  <Divider />
                  <Typography variant="subtitle2" mt={2}>
                    Histórico de atualizações
                  </Typography>
                  {item.history?.length ? (
                    item.history.map((entry, index) => (
                      <Box key={index} sx={{ borderLeft: "2px solid #16bc3f", pl: 2, mt: 2 }}>
                        <Typography variant="body2">{stageLabel(entry.stage)}</Typography>
                        <Typography variant="caption">
                          {new Date(entry.createdAt).toLocaleString("pt-BR")}
                        </Typography>
                        {entry.note && (
                          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                            {entry.note}
                          </Typography>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Nenhuma etapa de produção registrada pela equipe.
                    </Typography>
                  )}
                  {productionStages[stageIndex(item.stage) + 1] && (
                    <Button
                      sx={{ mt: 2 }}
                      variant="contained"
                      color="info"
                      disabled={cancelled(detail)}
                      onClick={() => begin(detail, item)}
                    >
                      Avançar para{" "}
                      {productionStages[stageIndex(item.stage) + 1].label.toLowerCase()}
                    </Button>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailKey(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(review)}
        onClose={() => {
          if (!busy) setReview(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Confirmar: {review?.next?.label}</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            Pedido #{review?.orderId} · {review?.item.title}
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Esta atualização ficará visível para o autor e será registrada no histórico.
          </Alert>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Observação para o autor (opcional)"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            inputProps={{ maxLength: 1000 }}
            disabled={busy}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setReview(null)}>
            Cancelar
          </Button>
          <Button disabled={busy} variant="contained" color="info" onClick={save}>
            {busy ? "Salvando…" : "Confirmar etapa"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
